import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import {
  Trophy, Flame, Sparkles, Moon, Star, Medal, Share2, Copy, Lock, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const ICON_MAP = { moon: Moon, flame: Flame, sparkles: Sparkles, trophy: Trophy, star: Star, medal: Medal };

const TIER_STYLES = {
  bronze: {
    ring: "from-amber-700 to-amber-500",
    text: "text-amber-300",
    chip: "bg-amber-950/70 text-amber-300 border-amber-700/40",
  },
  silver: {
    ring: "from-slate-400 to-slate-200",
    text: "text-slate-100",
    chip: "bg-slate-800/70 text-slate-200 border-slate-500/40",
  },
  gold: {
    ring: "from-yellow-500 to-amber-300",
    text: "text-yellow-200",
    chip: "bg-yellow-950/60 text-yellow-200 border-yellow-500/40",
  },
  platinum: {
    ring: "from-indigo-400 via-cyan-300 to-emerald-300",
    text: "text-indigo-100",
    chip: "bg-indigo-950/60 text-indigo-200 border-indigo-400/40",
  },
};

export default function BadgesShare() {
  const [data, setData] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareBadge, setShareBadge] = useState(null);

  useEffect(() => {
    http.get("/badges").then((r) => setData(r.data)).catch(() => setData({ badges: [], streak: {} }));
  }, []);

  if (!data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64 bg-white/10" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl bg-white/5" />)}
        </div>
      </div>
    );
  }

  const { streak, badges, earned_count, total, user_name, persona } = data;

  const openShare = (b) => { setShareBadge(b); setShareOpen(true); };

  return (
    <section className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-300" /> Streak Badges
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Unlock recovery badges as you build consistent sleep. Share them with your team to keep each other honest.
          </p>
        </div>
        <div className="flex gap-3">
          <HeaderStat label="Current streak" value={`${streak.current || 0}d`} tint="text-emerald-300" />
          <HeaderStat label="Longest" value={`${streak.longest || 0}d`} tint="text-amber-300" />
          <HeaderStat label="Earned" value={`${earned_count}/${total}`} tint="text-indigo-300" />
        </div>
      </div>

      {/* Streak progress ring */}
      <div className="circa-card p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
        <div className="grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-1 flex items-center justify-center">
            <StreakRing days={streak.current || 0} max={30} />
          </div>
          <div className="md:col-span-2">
            <div className="pill text-emerald-300 border-emerald-500/30 bg-emerald-950/50 mb-3">
              <Flame className="w-3.5 h-3.5" /> Recovery Streak
            </div>
            <div className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="badges-current-streak">
              {streak.current || 0}
              <span className="text-slate-500 text-xl font-medium"> / next milestone</span>
            </div>
            <p className="text-slate-400 text-sm mt-2 leading-relaxed max-w-lg">
              A recovery night counts when you log at least <strong className="text-slate-200">6h</strong> at quality
              <strong className="text-slate-200"> 4+</strong>. Miss a night and the counter resets — but your longest ever stays banked.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {[3, 7, 14, 30].map((m) => (
                <span key={m} className={`pill ${streak.current >= m ? "bg-emerald-950/70 border-emerald-500/40 text-emerald-200" : "bg-slate-900/60 border-white/10 text-slate-500"}`}>
                  {streak.current >= m && <CheckCircle2 className="w-3 h-3" />} {m}-day
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Badge grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="badges-grid">
        {badges.map((b) => (
          <BadgeCard key={b.id} badge={b} onShare={openShare} />
        ))}
      </div>

      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        badge={shareBadge}
        userName={user_name}
        persona={persona}
        streak={streak}
      />
    </section>
  );
}

function HeaderStat({ label, value, tint }) {
  return (
    <div className="circa-card px-4 py-3 min-w-[120px]">
      <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500">{label}</div>
      <div className={`font-display text-2xl font-bold ${tint}`}>{value}</div>
    </div>
  );
}

function StreakRing({ days, max }) {
  const pct = Math.min(1, days / max);
  const r = 70;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative w-44 h-44">
      <svg width="176" height="176" viewBox="0 0 176 176" className="-rotate-90">
        <circle cx="88" cy="88" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="10" fill="none" />
        <circle
          cx="88" cy="88" r={r}
          stroke="url(#ringGrad)"
          strokeWidth="10"
          strokeLinecap="round"
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          style={{ transition: "stroke-dashoffset 800ms ease" }}
        />
        <defs>
          <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#10B981" />
            <stop offset="100%" stopColor="#818CF8" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <Flame className="w-6 h-6 text-emerald-300 mb-1" />
        <div className="font-display text-4xl font-extrabold text-slate-100">{days}</div>
        <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500">day streak</div>
      </div>
    </div>
  );
}

function BadgeCard({ badge, onShare }) {
  const Icon = ICON_MAP[badge.icon] || Trophy;
  const tier = TIER_STYLES[badge.tier] || TIER_STYLES.silver;
  const pct = Math.round(badge.progress * 100);
  const earned = badge.earned;

  return (
    <div
      className={`circa-card p-5 relative overflow-hidden ${earned ? "ring-1 ring-indigo-400/30" : "opacity-90"}`}
      data-testid={`badge-card-${badge.id}`}
    >
      {earned && <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-transparent pointer-events-none" />}
      <div className="flex items-start gap-4">
        <div className="relative shrink-0">
          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${tier.ring} flex items-center justify-center ${earned ? "glow-indigo" : "grayscale opacity-60"}`}>
            <Icon className="w-7 h-7 text-white" />
          </div>
          {!earned && (
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-slate-900 border border-white/10 flex items-center justify-center">
              <Lock className="w-3 h-3 text-slate-500" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`font-display text-lg font-bold ${earned ? tier.text : "text-slate-300"}`}>{badge.name}</div>
            <span className={`pill text-[9px] ${tier.chip}`}>{badge.tier}</span>
          </div>
          <p className="text-slate-400 text-xs mt-1 leading-relaxed">{badge.description}</p>

          <div className="mt-3">
            <div className="flex justify-between text-[10px] font-mono-ui uppercase tracking-widest text-slate-500 mb-1">
              <span>{badge.value} / {badge.threshold}</span>
              <span>{pct}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-800/80 overflow-hidden">
              <div className={`h-full ${earned ? "bg-gradient-to-r from-emerald-400 to-indigo-400" : "bg-indigo-500/50"}`} style={{ width: `${pct}%` }} />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              size="sm"
              variant={earned ? "default" : "outline"}
              disabled={!earned}
              onClick={() => onShare(badge)}
              data-testid={`badge-share-${badge.id}`}
              className={earned
                ? "rounded-full bg-indigo-500 hover:bg-indigo-400 text-white"
                : "rounded-full bg-transparent border-white/10 text-slate-500"}
            >
              <Share2 className="w-3.5 h-3.5 mr-1" /> {earned ? "Share" : "Locked"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShareDialog({ open, onOpenChange, badge, userName, persona, streak }) {
  const shareText = useMemo(() => {
    if (!badge) return "";
    return (
      `🏅 I just unlocked "${badge.name}" on CircaShift.\n` +
      `${badge.description}\n` +
      `Current streak: ${streak?.current || 0} nights · Longest: ${streak?.longest || 0} nights.\n` +
      `Building sleep that bends around my shifts — one recovery night at a time.`
    );
  }, [badge, streak]);

  if (!badge) return null;
  const Icon = ICON_MAP[badge.icon] || Trophy;
  const tier = TIER_STYLES[badge.tier] || TIER_STYLES.silver;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Copied to clipboard", { description: "Paste it into Slack, WhatsApp, or your team channel." });
    } catch {
      toast.error("Couldn't copy — long-press to select the text.");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `CircaShift · ${badge.name}`, text: shareText });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F141F] border-white/10 text-slate-100 sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold">Share your badge</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            Nudge a teammate to protect their sleep too.
          </DialogDescription>
        </DialogHeader>

        {/* Preview card */}
        <div className="relative rounded-2xl p-5 overflow-hidden border border-white/10 bg-gradient-to-br from-slate-900 to-slate-950">
          <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-indigo-500/20 blur-3xl" />
          <div className="flex items-center gap-4 relative">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${tier.ring} flex items-center justify-center glow-indigo`}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-400">CircaShift</div>
              <div className="font-display text-xl font-extrabold">{badge.name}</div>
              <div className="text-xs text-slate-400 mt-0.5">{userName}{persona ? ` · ${persona}` : ""}</div>
            </div>
          </div>
          <div className="relative mt-4 grid grid-cols-3 gap-2">
            <MiniStat label="Streak" value={`${streak?.current || 0}d`} />
            <MiniStat label="Longest" value={`${streak?.longest || 0}d`} />
            <MiniStat label="Tier" value={badge.tier} />
          </div>
        </div>

        <div className="rounded-xl bg-slate-900/60 border border-white/10 p-3 text-xs text-slate-300 whitespace-pre-wrap font-mono-ui" data-testid="share-preview-text">
          {shareText}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" onClick={copy} data-testid="share-copy-btn" className="rounded-full bg-transparent border-white/15 text-slate-200 hover:bg-white/5">
            <Copy className="w-4 h-4 mr-1" /> Copy
          </Button>
          <Button onClick={nativeShare} data-testid="share-native-btn" className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo">
            <Share2 className="w-4 h-4 mr-1" /> Share
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-slate-900/60 border border-white/10 rounded-xl p-2 text-center">
      <div className="text-[9px] uppercase tracking-widest font-mono-ui text-slate-500">{label}</div>
      <div className="font-display text-sm font-bold text-slate-100">{value}</div>
    </div>
  );
}
